import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../../constants/common'
import Button, { BUTTON_HEIGHT_40 } from '../../../Button'
import { EntityStatus } from '../../../Entity'
import { APPLICANT_STAGES } from '../../../RentalPortalApplicant'
import { ButtonSelect, ButtonTextSelectControl } from '../../../Select'
import { Skeleton } from '../../../Skeleton'
import { useApplicantProfileContext } from '../RentalPortalApplicantProfile.hooks'

import {
  APPLICANT_STAGE_FALLBACK_TEXT_COLOR,
  QA_NAME_APPLICANT_PROFILE_STAGE_BADGE,
  QA_NAME_APPLICANT_PROFILE_STAGE_SELECT,
} from './RentalPortalApplicantProfileHeaderStageSelect.constants'
import {
  useApplicantConfirmedTransition,
  useApplicantMoveToScreening,
} from './RentalPortalApplicantProfileHeaderStageSelect.hooks'
import {
  getApplicantConfirmedTransition,
  getApplicantStageSelectOptions,
  getApplicantStageSelectValue,
  getHasEnabledApplicantStageOption,
  getIsApplicantMoveToScreeningOption,
} from './RentalPortalApplicantProfileHeaderStageSelect.utils'

import * as styles from './RentalPortalApplicantProfileHeaderStageSelect.module.css'

function RentalPortalApplicantProfileHeaderStageSelect({
  className,
  height,
  isLoading,
  textClassName,
}) {
  const { t } = useTranslation()

  const { applicantData, isPreview } = useApplicantProfileContext()

  const { handleClickMoveToScreening, isMovingToScreening } =
    useApplicantMoveToScreening()

  const { handleClickConfirmedTransition, isTransitionSaving } =
    useApplicantConfirmedTransition()

  const isStageSaving = isMovingToScreening || isTransitionSaving

  const stageValue = getApplicantStageSelectValue({
    stage: applicantData?.stage,
    t,
  })

  const stageOptions = getApplicantStageSelectOptions({ applicantData, t })

  function handleSelectStage(option) {
    const confirmedTransition = getApplicantConfirmedTransition(option)

    if (getIsApplicantMoveToScreeningOption(option)) {
      handleClickMoveToScreening()
    } else if (confirmedTransition) {
      handleClickConfirmedTransition(confirmedTransition)
    }
  }

  return isLoading ? (
    <Skeleton className={classNames(styles.skeleton, className)} />
  ) : !isPreview && getHasEnabledApplicantStageOption(stageOptions) ? (
    <ButtonSelect
      Control={ButtonTextSelectControl}
      className={className}
      controlProps={{
        buttonProps: {
          Button: Button.Select,
          className: styles.button,
          disabled: isStageSaving,
          height: BUTTON_HEIGHT_40,
          qaName: QA_NAME_APPLICANT_PROFILE_STAGE_SELECT,
          style: {
            '--applicant-profile-header-stage-color':
              stageValue?.textColor || APPLICANT_STAGE_FALLBACK_TEXT_COLOR,
          },
          text: stageValue?.label,
          textClassName: styles.buttonText,
        },
      }}
      disabled={isStageSaving}
      dropdownFloatingConfig={{
        dropdownPlacement: FLOATING_ELEMENT_PLACEMENT.bottom,
        maxWidthByRef: false,
      }}
      dropdownProps={{ className: styles.dropdown }}
      listProps={{ headerProps: { Search: null } }}
      options={stageOptions}
      qaName={QA_NAME_APPLICANT_PROFILE_STAGE_SELECT}
      value={stageValue}
      onSelectItem={handleSelectStage}
    />
  ) : (
    <EntityStatus
      className={classNames(className, styles.badge)}
      height={height}
      id={applicantData?.stage}
      statuses={APPLICANT_STAGES}
      textClassName={textClassName}
      textQaName={QA_NAME_APPLICANT_PROFILE_STAGE_BADGE}
    />
  )
}

export default RentalPortalApplicantProfileHeaderStageSelect
